/**
 * SNSDetector - URL Detection and Analysis
 * Handles detection of SNS URLs in content
 */

import { Patterns } from "../../constants/patterns";

interface SnsPatternEntry {
    regex: RegExp;
    icon?: string;
    color?: string;
    name?: string;
    embedSupport?: boolean;
}

export interface DetectedSnsMatch {
    fullMatch: string;
    id: string | null;
    url: string;
    index: number;
}

export type DetectedLinksMap = Record<string, DetectedSnsMatch[]>;

class SNSDetector {
    public patterns: Record<string, SnsPatternEntry>;
    public detectedLinks: DetectedLinksMap;

    public constructor(patterns: Record<string, SnsPatternEntry> | null = null) {
        this.patterns = patterns || (Patterns.SNS_PLATFORMS as Record<string, SnsPatternEntry>);
        this.detectedLinks = {};
    }

    /**
     * Detect all SNS links in content
     */
    public detectLinks(content: string): DetectedLinksMap {
        if (!content || typeof content !== "string") {
            return {};
        }

        const detectedLinks: DetectedLinksMap = {};

        try {
            Object.keys(this.patterns).forEach((platform) => {
                try {
                    const pattern = this.patterns[platform];
                    if (!pattern?.regex) return;

                    pattern.regex.lastIndex = 0;

                    const matches: RegExpExecArray[] = [];
                    let match: RegExpExecArray | null;

                    while ((match = pattern.regex.exec(content)) !== null) {
                        matches.push(match);
                        if (!pattern.regex.global) break;
                    }

                    if (matches.length > 0) {
                        detectedLinks[platform] = matches.map((item) => ({
                            fullMatch: item[0],
                            id: item[1] || null,
                            url: item[0],
                            index: item.index,
                        }));
                    }
                } catch (error) {
                    console.error("Pattern matching failed:", error);
                }
            });
        } catch (error) {
            console.error("SNS detection failed:", error);
        }

        return detectedLinks;
    }

    /**
     * Extract SNS URLs from content.
     *
     * Notes:
     * - Decodes a small subset of HTML entities (via Patterns.decodeHtmlEntities).
     * - If the content contains Facebook plugin URLs (e.g. /plugins/post.php?href=...),
     *   it will prefer the decoded `href=` original URL.
     */
    public extractUrls(
        content: string,
        options: { decodeHtmlEntities?: boolean; expandFacebookPluginHref?: boolean } = {},
    ): string[] {
        const { decodeHtmlEntities = true, expandFacebookPluginHref = true } = options;
        if (!content || typeof content !== "string") return [];

        if (
            !content.includes("facebook") &&
            !content.includes("youtu") &&
            !content.includes("instagram") &&
            !content.includes("twitter") &&
            !content.includes("x.com") &&
            !content.includes("tiktok")
        ) {
            return [];
        }

        const text = decodeHtmlEntities ? Patterns.decodeHtmlEntities(content) : content;
        const detected = this.detectLinks(text);
        const urls: string[] = [];

        Object.values(detected).forEach((matches) => {
            if (!Array.isArray(matches)) return;
            matches.forEach((match) => {
                if (match && typeof match.url === "string") urls.push(match.url);
            });
        });

        const normalized = urls
            .map((url) => {
                const value = String(url || "");
                if (!expandFacebookPluginHref) return value;

                if (value.includes("facebook.com/plugins/") || value.includes("fb.com/plugins/")) {
                    const hrefMatch = value.match(Patterns.IFRAME_HREF_QUERY_PARAM);
                    if (hrefMatch && hrefMatch[1]) {
                        try {
                            return decodeURIComponent(hrefMatch[1]);
                        } catch {
                            return hrefMatch[1];
                        }
                    }
                }

                return value;
            })
            .filter((value) => value && !value.includes("/plugins/"));

        return [...new Set(normalized)];
    }

    /**
     * Detect platform/id for a single URL string.
     */
    public detectUrlMeta(url: string): { platform: string | null; id: string | null; url: string } {
        let normalizedUrl = String(url || "").trim();
        if (!normalizedUrl) return { platform: null, id: null, url: "" };

        try {
            normalizedUrl = Patterns.decodeHtmlEntities(normalizedUrl);
        } catch {
            void 0;
        }
        try {
            normalizedUrl = Patterns.decodeAmpersand(normalizedUrl);
        } catch {
            void 0;
        }

        for (const [platform, pattern] of Object.entries(this.patterns)) {
            pattern.regex.lastIndex = 0;
            const match = pattern.regex.exec(normalizedUrl);
            if (match) {
                return { platform, id: match[1] || null, url: match[0] || normalizedUrl };
            }
        }

        return { platform: null, id: null, url: normalizedUrl };
    }

    /**
     * Check if content has any SNS links
     */
    public hasLinks(content: string): boolean {
        const detected = this.detectLinks(content);
        return Object.keys(detected).length > 0;
    }

    /**
     * Get all detected platforms
     */
    public getDetectedPlatforms(content: string): string[] {
        const detected = this.detectLinks(content);
        return Object.keys(detected);
    }

    /**
     * Detect platform from a single URL
     */
    public detectPlatform(url: string): string | null {
        if (!url || typeof url !== "string") {
            return null;
        }

        for (const [platform, pattern] of Object.entries(this.patterns)) {
            pattern.regex.lastIndex = 0;
            if (pattern.regex.test(url)) {
                return platform;
            }
        }

        return null;
    }

    /**
     * Get links for specific platform
     */
    public getLinksForPlatform(content: string, platform: string): DetectedSnsMatch[] {
        const detected = this.detectLinks(content);
        return detected[platform] || [];
    }

    /**
     * Debug function to test specific patterns
     */
    public testPattern(content: string, platform: string): boolean {
        const pattern = this.patterns[platform];
        if (!pattern) {
            return false;
        }

        pattern.regex.lastIndex = 0;
        const match = pattern.regex.exec(content);

        return match !== null;
    }
}

export { SNSDetector };
